import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material'
import { alpha, useTheme } from '@mui/material/styles'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useTranslation } from 'react-i18next'
import { useLocale } from '@/contexts/LocaleContext'
import { LANGUAGE_INTL_LOCALE } from '@/i18n/types'
import type { Plan, PlanPackage } from '@/services/billingService'
import IncludesList from './IncludesList'
import PriceTag from './PriceTag'

/**
 * Where this plan sits relative to the one the account is on.
 *  - `current`  — theirs. The plan itself cannot be bought again.
 *  - `included` — below theirs; they already have everything in it.
 *  - `available`— a plan they could move to.
 */
export type PlanStanding = 'current' | 'included' | 'available'

type PlanCardProps = {
  plan: Plan
  standing: PlanStanding
  /** At most one card on the screen carries this. */
  recommended: boolean
  /** Chosen once for the whole page — see BillingPeriodToggle. */
  selectedMonths: number
  onBuy: (pkg: PlanPackage) => void
  /** This card's purchase is in flight. */
  busy: boolean
  /** Where the plan that is agreed with a person is agreed. */
  contactHref: string
  /** Why the last attempt to buy this one did not go through. */
  error?: string
}

/**
 * One plan: its name, its price, what it contains, and the single thing you
 * can do about it.
 *
 * The action is decided from the payload rather than from the plan's name. A
 * plan you are already on offers no way to buy it again — except a one-off
 * respondent package, which tops up a pool rather than buying the plan a
 * second time, and says so. A plan sold by talking to a person offers the
 * conversation instead of a checkout.
 */
/** One height for all three actions, so the lists underneath start level. */
const ACTION_SX = { py: 1.1, fontSize: 14, fontWeight: 700 } as const

export default function PlanCard({
  plan,
  standing,
  recommended,
  selectedMonths,
  onBuy,
  busy,
  contactHref,
  error,
}: PlanCardProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('plans')
  const { language } = useLocale()

  const planName = t(`plan.${plan.id}.name`, { defaultValue: plan.id })
  // The page picks the period; a plan that does not sell it falls back to what
  // it has. Enterprise has no packages at all and shows a quote instead.
  const selected =
    plan.packages.find((pkg) => pkg.months === selectedMonths) ?? plan.packages[0] ?? null

  // A one-off package is a top-up; a monthly one is the plan itself. That
  // distinction, not the plan's id, decides both the wording and whether the
  // current plan still has something to sell.
  const isTopUp = selected !== null && selected.period === null
  // Announced, not released: everything on the card is drawn — price, packages,
  // what it contains — and only the button changes, because somebody has to be
  // able to see what they will be able to grow into. `=== false` on purpose:
  // an older backend sends no flag, and the plans it serves are all buyable.
  const comingSoon = plan.on_sale === false && standing !== 'current'
  const canBuy =
    plan.self_serve &&
    !comingSoon &&
    selected !== null &&
    (standing === 'available' || (standing === 'current' && isTopUp))

  // Every card quotes a price per month, whichever period is selected, because
  // that is the only figure the three can be compared on. What actually leaves
  // the card is said once under the period toggle, not three times down the row.
  // `count` drives the plural, `formatted` is what is printed: 10 000 rather
  // than 10000, grouped the way the reader's language groups numbers.
  const caption = selected
    ? t('packages.perMonth', {
        count: selected.respondents,
        formatted: selected.respondents.toLocaleString(LANGUAGE_INTL_LOCALE[language]),
      })
    : t('price.customHint')

  return (
    <Box
      sx={{
        bgcolor: c.white,
        border: `1px solid ${
          recommended ? c.coral : standing === 'current' ? c.ink32 : c.ink08
        }`,
        borderRadius: `${theme.affexy.radii.lg}px`,
        p: { xs: 2.5, md: 3 },
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        boxShadow: '0 1px 2px rgba(27,28,56,.04)',
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography
          component="h2"
          sx={{
            fontFamily: theme.affexy.fonts.heading,
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: '-0.01em',
          }}
        >
          {planName}
        </Typography>
        {standing === 'current' ? (
          <Chip
            size="small"
            icon={<CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
            label={t('badge.current')}
            sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: c.gray100, color: c.ink72 }}
          />
        ) : comingSoon ? (
          <Chip
            size="small"
            label={t('badge.comingSoon')}
            sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: c.gray100, color: c.ink72 }}
          />
        ) : recommended ? (
          <Chip
            size="small"
            label={t('badge.recommended')}
            sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: c.coralSoft, color: c.coralHover }}
          />
        ) : null}
      </Stack>

      <Typography sx={{ fontSize: 13.5, lineHeight: 1.45, color: c.ink56, minHeight: 39 }}>
        {t(`plan.${plan.id}.tagline`, { defaultValue: '' })}
      </Typography>

      <PriceTag
        amount={selected?.price_per_month_pln ?? null}
        period={selected?.period ?? null}
        caption={caption}
      />

      <Stack spacing={1}>
        {/* Every card puts a button in the same place, at the same size, even
            the one nobody can press. A card that states its status in a
            sentence instead is a card whose feature list starts lower than its
            neighbours', and three lists that do not line up cannot be compared. */}
        {canBuy && selected ? (
          <Button
            variant="contained"
            fullWidth
            disabled={busy}
            onClick={() => onBuy(selected)}
            sx={ACTION_SX}
          >
            {busy
              ? t('actions.working')
              : standing === 'current'
                ? t('actions.topUp')
                : isTopUp
                  ? t('actions.buy')
                  : t('actions.upgrade', { plan: planName })}
          </Button>
        ) : comingSoon ? (
          <Button variant="outlined" fullWidth disabled sx={ACTION_SX}>
            {t('actions.comingSoon')}
          </Button>
        ) : !plan.self_serve && standing !== 'current' ? (
          <Button variant="outlined" fullWidth component="a" href={contactHref} sx={ACTION_SX}>
            {t('actions.contact')}
          </Button>
        ) : (
          <Button
            variant="outlined"
            fullWidth
            disabled
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 17 }} />}
            sx={ACTION_SX}
          >
            {standing === 'current' ? t('card.currentPlan') : t('card.alreadyIncluded')}
          </Button>
        )}

        {/* No sentence explaining the disabled button, and none explaining that
            respondents refill monthly. The "Wkrótce" chip already says the
            first; the price caption already says the second. */}
        {error && (
          <Box
            sx={{
              p: 1.5,
              borderRadius: '12px',
              bgcolor: alpha(c.coral, 0.05),
              border: `1px solid ${alpha(c.coral, 0.25)}`,
              fontSize: 13,
            }}
          >
            {error}
          </Box>
        )}
      </Stack>

      <Divider sx={{ borderColor: c.ink08 }} />

      <IncludesList includes={plan.includes} />
    </Box>
  )
}
